import {
    Box,
    Button,
    CircularProgress,
    Link,
    Typography,
} from '@deere/fuel-react';
import { Breadcrumbs } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AlertBox } from '../../components/AlertBox';
import { JobDetails } from '../../components/JobDetails.jsx';
import { LoggedInContext } from '../../context/LoggedInContext';
import { errorBannerFromResponse } from '../../utils/errorBanner';
import { WorkforceContext } from '../../context/WorkforceContext.jsx';
import { ALLOWED_BIDDING_GROUPS_TO_WITHDRAW } from '../../constants';
import styles from '../Admin/ActiveJobDetails.module.css';
import { WithdrawBidModal } from '../../components/WithdrawBidModal';

export const WorkforceActiveJobDetails = () => {
    const { jobNumber } = useParams();
    const [bid, setBid] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const [alertMsg, setAlertMsg] = useState();
    const [alertVariant, setAlertVariant] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { authState, impersonatedId, makeRequest } =
        useContext(LoggedInContext);
    const { setTriggerRefresh } = useContext(WorkforceContext);

    useEffect(() => {
        let ignore = false;
        const fetchActiveSelectedBid = async () => {
            setIsLoading(true);
            try {
                if (!ignore) {
                    const bidResponse = await makeRequest(
                        process.env.REACT_APP_API_ENDPOINT +
                            '/bid/activeSelected/' +
                            jobNumber
                    );
                    if (bidResponse.status === 200) {
                        const bidDetails = await bidResponse.json();
                        if (
                            bidDetails.status === 'SUBMITTED' &&
                            ALLOWED_BIDDING_GROUPS_TO_WITHDRAW.includes(
                                bidDetails.biddingGroupId
                            )
                        ) {
                            setAlertMsg(
                                'You can cancel your bid on this job by clicking on the button below.'
                            );
                            setAlertVariant('info');
                        } else if (bidDetails.status === 'WITHDRAWN') {
                            setAlertMsg(
                                'You have successfully withdrawn from this job'
                            );
                            setAlertVariant('success');
                        } else if (bidDetails.status === 'SUBMITTED') {
                            setAlertMsg(
                                'You have already successfully bid on this job. If you would like to cancel your bid, you must wait until the Bid Withdrawal Period starts, revisit this page and withdraw your bid.'
                            );
                            setAlertVariant('success');
                        }
                        setBid(bidDetails);
                    } else {
                        setAlertMsg(
                            errorBannerFromResponse(
                                bidResponse,
                                'We are unable to retrieve active bidders for this job. Please try again later.'
                            ).message
                        );
                        setAlertVariant('error');
                    }
                    setIsLoading(false);
                }
            } catch (err) {
                setAlertMsg(
                    errorBannerFromResponse(
                        err,
                        'We are unable to retrieve this job bid. Please try again later.'
                    ).message
                );
                setAlertVariant('error');
            }
        };

        fetchActiveSelectedBid();

        // cleanup function that will ignore future results of fetch triggered by the useEffect hook
        return () => {
            ignore = true;
        };
    }, [jobNumber, authState, impersonatedId, makeRequest]);

    const bidOnthisJob = async () => {
        const bidOnthisJobResponse = await makeRequest(
            process.env.REACT_APP_API_ENDPOINT +
                '/job/' +
                jobNumber +
                '/bidders',
            {
                method: 'POST',
                body: JSON.stringify({
                    racf: impersonatedId
                        ? impersonatedId
                        : authState.accessToken.claims.userID.toLowerCase(),
                    status: 'SUBMITTED',
                }),
            }
        );
        if (bidOnthisJobResponse.status === 200) {
            const responseData = await bidOnthisJobResponse.json();
            setTriggerRefresh((t) => !t);
            if (responseData.preference > 1) {
                navigate('/my-job-bids', {
                    state: {
                        variant: 'success',
                        message: `You have successfully bid on job ${jobNumber}.`,
                    },
                });
            } else {
                navigate('/active-job-openings', {
                    state: {
                        variant: 'success',
                        message:
                            'You have successfully bid on job ' +
                            jobNumber +
                            '. This job is automatically ranked as your #1 preference.',
                    },
                });
            }
        } else {
            setAlertMsg(
                errorBannerFromResponse(
                    bidOnthisJobResponse,
                    'Your bid on this job was not saved. Please try again later.'
                ).message
            );
            setAlertVariant('error');
        }
    };

    return (
        <div className={styles.pageLevelParent}>
            <Box spacing={6} className={styles.pageLevelContent}>
                <Breadcrumbs aria-label="Breadcrumbs">
                    <Link href="/active-job-openings">Active Job Openings</Link>
                    <Typography color="textPrimary">Job {jobNumber}</Typography>
                </Breadcrumbs>
                <Box className={styles.spaceOnTop}>
                    {location.state?.message && (
                        <AlertBox
                            msg={location.state.message}
                            variant={location.state.variant}
                        />
                    )}
                    {!location.state?.message && alertVariant && (
                        <AlertBox msg={alertMsg} variant={alertVariant} />
                    )}
                </Box>
                {!isLoading && bid && (
                    <Box className={styles.boxForButton}>
                        <Typography
                            variant="h1"
                            className={styles.pageLevelHeading}
                        >
                            #{jobNumber}
                        </Typography>
                    </Box>
                )}
                {isLoading && (
                    <CircularProgress
                        size="lg"
                        variant="indeterminate"
                        title={'Loading'}
                    />
                )}
                {!isLoading && bid && <JobDetails bidObj={bid} />}
                {!location.state?.message &&
                    !isLoading &&
                    bid &&
                    bid.status !== 'SUBMITTED' &&
                    bid.status !== 'WITHDRAWN' && (
                        <Box mt={2} justifyContent="flex-end" display="flex">
                            <Button
                                variant="primary"
                                className={styles.pageLevelButton}
                                onClick={bidOnthisJob}
                            >
                                Bid on this Job
                            </Button>
                        </Box>
                    )}
                {!location.state?.message &&
                    !isLoading &&
                    bid &&
                    bid.status === 'SUBMITTED' &&
                    ALLOWED_BIDDING_GROUPS_TO_WITHDRAW.includes(
                        bid.biddingGroupId
                    ) && (
                        <Box mt={2} justifyContent="flex-end" display="flex">
                            <WithdrawBidModal
                                jobNumber={jobNumber}
                                bidId={bid.id}
                                apiResponse={(resp, errorResponse) => {
                                    if (resp === 'error') {
                                        setAlertVariant('error');
                                        setAlertMsg(
                                            errorBannerFromResponse(
                                                errorResponse,
                                                'Due to technical issues, we are unable to withdraw this job bid. Please try again later.'
                                            ).message
                                        );
                                    }
                                }}
                            />
                        </Box>
                    )}
            </Box>
        </div>
    );
};
